import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { fmtKg, fmtUsd } from "@/lib/format";
import { CalendarBlank, Lightning, LockKey, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const PACKAGE_COLOR = {
  Substructure: "#6E59C7",
  Superstructure: "#D4AF37",
  Envelope: "#38BDF8",
  Facade: "#10B981",
  Roof: "#F59E0B",
  MEP: "#EF4444",
  Internal: "#A78BFA",
  Finishes: "#F472B6",
  Externals: "#94A3B8",
  Other: "#64748B",
};

function daysBetween(a, b) {
  return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
}

export default function Schedule() {
  const { active } = useProjects();
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");

  const load = async (programmeStart) => {
    if (!active) return;
    const url = `/projects/${active.id}/schedule${programmeStart ? `?start=${programmeStart}` : ""}`;
    const { data } = await api.get(url);
    setData(data);
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [active]);

  const { totalDays, programmeStartDate, programmeEndDate } = useMemo(() => {
    if (!data) return { totalDays: 1, programmeStartDate: null, programmeEndDate: null };
    return {
      totalDays: daysBetween(data.programme_start, data.programme_end),
      programmeStartDate: data.programme_start,
      programmeEndDate: data.programme_end,
    };
  }, [data]);

  const onUpdateStart = async () => {
    if (!start) return toast.error("Pick a start date");
    await load(start);
    toast.success("Programme re-baselined");
  };

  if (!active) {
    return <div className="px-8 py-12 theme-ink-muted">Select a project to view its 5D schedule.</div>;
  }

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · 5D Carbon × Cost × Schedule"
        title="Programme Twin"
        subtitle="Gantt overlay linking each work package to its embodied carbon, cost, and procurement lock-in window. Switch packages before the bar turns gold-and-locked."
        kicker={
          data
            ? `${data.tasks.length} PACKAGES · ${programmeStartDate} → ${programmeEndDate} · ${fmtKg(data.total_embodied_kg)} EMBODIED`
            : "LOADING…"
        }
        guideId="schedule"
        action={
          <div className="flex items-center gap-2">
            <CalendarBlank size={14} className="theme-ink-dim" />
            <input
              data-testid="schedule-start-input"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="theme-input border text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-gold"
            />
            <button
              data-testid="schedule-rebaseline-btn"
              onClick={onUpdateStart}
              className="text-xs uppercase tracking-wider border border-gold/40 text-gold hover:bg-gold/10 px-3 py-1.5 rounded-sm transition-colors"
            >
              Re-baseline
            </button>
          </div>
        }
      />

      {data && (
        <>
          {/* Summary strip */}
          <section className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryTile label="Total embodied" value={fmtKg(data.total_embodied_kg)} accent="text-gold" />
            <SummaryTile label="Total carbon cost" value={fmtUsd(data.total_carbon_cost_usd)} accent="text-emerald-400" />
            <SummaryTile label="Carbon price" value={`$${data.carbon_price_usd_per_t}/t`} />
            <SummaryTile label="Programme span" value={`${totalDays} days`} />
          </section>

          {/* Gantt */}
          <section className="px-8 pt-8" data-testid="gantt-chart">
            <div className="border border theme-border theme-surface">
              <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b theme-border">
                <div>
                  <div className="overline">Work Packages × Time</div>
                  <h3 className="font-serif-display text-2xl theme-ink mt-1">Gantt overlay</h3>
                </div>
                <div className="text-[11px] font-mono-data theme-ink-dim">
                  Bar width = duration · Bar fill = embodied share · Lock icon = procurement frozen
                </div>
              </div>

              <div className="p-6 overflow-x-auto">
                <div className="min-w-[720px] flex flex-col gap-2">
                  {/* Timeline ruler */}
                  <div className="grid grid-cols-12 text-[10px] uppercase tracking-wider theme-ink-dim mb-2">
                    {Array.from({ length: 12 }).map((_, i) => {
                      const d = new Date(programmeStartDate);
                      d.setDate(d.getDate() + Math.round((i / 12) * totalDays));
                      return (
                        <div key={i} className="text-left border-l theme-border pl-1">
                          {d.toISOString().slice(0, 7)}
                        </div>
                      );
                    })}
                  </div>

                  {data.tasks.map((t) => {
                    const offsetDays = daysBetween(programmeStartDate, t.start_date) - 1;
                    const durDays = daysBetween(t.start_date, t.end_date);
                    const left = (offsetDays / totalDays) * 100;
                    const width = Math.max(3, (durDays / totalDays) * 100);
                    const color = PACKAGE_COLOR[t.package] || "#64748B";

                    return (
                      <div
                        key={t.package}
                        data-testid={`gantt-task-${t.package.toLowerCase()}`}
                        className="grid grid-cols-[180px_1fr_120px] items-center gap-3 py-1.5"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="inline-block h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-sm theme-ink truncate">{t.package}</span>
                          {t.critical_path && (
                            <Lightning size={12} weight="fill" className="text-gold shrink-0" />
                          )}
                          {t.locked_pct >= 50 && (
                            <LockKey size={12} className="theme-ink-dim shrink-0" />
                          )}
                        </div>

                        <div className="relative h-7 bg-black/10 dark:bg-white/[0.03] rounded-sm">
                          <div
                            className="absolute top-0 bottom-0 rounded-sm flex items-center px-2 text-[11px] font-mono-data text-white/90 group cursor-pointer transition-all hover:brightness-110"
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              backgroundColor: color,
                              opacity: 0.85,
                            }}
                            title={`${t.package} · ${t.start_date} → ${t.end_date} · ${fmtKg(t.embodied_kg)} · ${fmtUsd(t.carbon_cost_usd)} carbon cost · ${t.locked_pct}% locked`}
                          >
                            <span className="truncate">{fmtKg(t.embodied_kg)}</span>
                          </div>
                          {/* Locked overlay */}
                          {t.locked_pct > 0 && (
                            <div
                              className="absolute top-0 bottom-0 rounded-l-sm"
                              style={{
                                left: `${left}%`,
                                width: `${(width * t.locked_pct) / 100}%`,
                                background:
                                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.2) 0px, rgba(255,255,255,0.2) 3px, transparent 3px, transparent 6px)",
                              }}
                            />
                          )}
                        </div>

                        <div className="text-right text-[11px] font-mono-data theme-ink-muted">
                          {t.duration_weeks}w · {t.share_pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Detail table */}
          <section className="px-8 pt-6 pb-12">
            <div className="border border theme-border theme-surface overflow-x-auto">
              <table className="w-full text-sm" data-testid="schedule-table">
                <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                  <tr>
                    <th className="text-left px-6 py-3">Package</th>
                    <th className="text-left px-3 py-3">Start</th>
                    <th className="text-left px-3 py-3">End</th>
                    <th className="text-right px-3 py-3">Items</th>
                    <th className="text-right px-3 py-3">Embodied</th>
                    <th className="text-right px-3 py-3">Carbon cost</th>
                    <th className="text-right px-3 py-3">Locked</th>
                    <th className="text-right px-6 py-3">Critical</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tasks.map((t) => (
                    <tr key={t.package} className="border-t border-white/5">
                      <td className="px-6 py-3 theme-ink">
                        <span
                          className="inline-block h-2 w-2 rounded-full mr-2"
                          style={{ backgroundColor: PACKAGE_COLOR[t.package] || "#64748B" }}
                        />
                        {t.package}
                      </td>
                      <td className="px-3 font-mono-data theme-ink-muted">{t.start_date}</td>
                      <td className="px-3 font-mono-data theme-ink-muted">{t.end_date}</td>
                      <td className="px-3 text-right font-mono-data theme-ink-muted">{t.items_count}</td>
                      <td className="px-3 text-right font-mono-data text-gold">{fmtKg(t.embodied_kg)}</td>
                      <td className="px-3 text-right font-mono-data text-emerald-400">{fmtUsd(t.carbon_cost_usd)}</td>
                      <td className="px-3 text-right font-mono-data theme-ink-muted">{t.locked_pct}%</td>
                      <td className="px-6 py-3 text-right">
                        {t.critical_path ? (
                          <Lightning size={14} weight="fill" className="text-gold inline" />
                        ) : (
                          <span className="theme-ink-dim text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, accent = "theme-ink" }) {
  return (
    <div className="theme-surface border border theme-border p-4">
      <div className="overline">{label}</div>
      <div className={`font-mono-data text-xl mt-2 ${accent}`}>{value}</div>
    </div>
  );
}
